import { FieldInfo } from '@/components/FieldInfo';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Input } from '@nautilusbio/nb-design-system/Input';
import { Stack, getStackClassName } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import type { TextAsOptions } from '@nautilusbio/nb-design-system/Text';
import { Textarea } from '@nautilusbio/nb-design-system/Textarea';
import { useForm } from '@tanstack/react-form';
import { type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { Check, Loader, PencilLineIcon } from 'lucide-react';
import React, { useState } from 'react';

import createStyles from '../../Create/styles.module.css';

import styles from './styles.module.css';

export type EditInPlaceProps<TData = Record<string, unknown>, TVariables = Record<string, unknown>> = {
  'aria-label': string;
  as: TextAsOptions;
  cacheKey: readonly string[];
  canEdit?: boolean;
  fieldName: string;
  mutationFn: (variables: TVariables) => Promise<TData>;
  onCacheUpdate?: (queryClient: QueryClient, data: TData, variables: TVariables) => void;
  onInvalidateQueries?: (queryClient: QueryClient) => void;
  type: 'input' | 'textarea';
  value: string;
};

const EditInPlace = <
  TData extends Record<string, unknown> = Record<string, unknown>,
  TVariables extends Record<string, unknown> = Record<string, unknown>,
>({
  'aria-label': ariaLabel,
  as,
  cacheKey,
  canEdit = true,
  fieldName,
  mutationFn,
  onCacheUpdate,
  onInvalidateQueries,
  type,
  value,
}: EditInPlaceProps<TData, TVariables>) => {
  const [isEditing, setIsEditing] = useState(false);
  const queryClient = useQueryClient();

  const editMutation = useMutation<TData, Error, TVariables, { previousData?: TData }>({
    mutationFn,
    onMutate: async variables => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: cacheKey });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<TData>(cacheKey);

      // Optimistically update the cache
      queryClient.setQueryData<TData>(cacheKey, old => {
        if (!old) return old;

        // Only update the field specified by fieldName with the value from variables
        return { ...old, [fieldName]: variables[fieldName] };
      });

      // Return context with the previous data
      return { previousData };
    },
    onError: (_err, _variables, context) => {
      // Rollback to the previous value on error
      if (context?.previousData) {
        queryClient.setQueryData(cacheKey, context.previousData);
      }

      // Exit editing mode on error so user can see the original value
      setIsEditing(false);
      form.reset();
    },
    onSuccess: (data, variables) => {
      // Update the cache with the server response
      queryClient.setQueryData<TData>(cacheKey, oldData => {
        if (!oldData) return data;
        return { ...oldData, ...data };
      });

      // Call custom cache update handler if provided (e.g., to update list cache)
      if (onCacheUpdate) {
        onCacheUpdate(queryClient, data, variables);
      }

      // Exit editing mode and reset form on success
      setIsEditing(false);
      form.reset();
    },
    onSettled: () => {
      // Call custom invalidation handler if provided (e.g., for DELETE operations)
      // Note: We don't invalidate cacheKey here because onSuccess already updated
      // the cache directly with the server response. Invalidating would cause an
      // unnecessary GET request after the PATCH.
      if (onInvalidateQueries) {
        onInvalidateQueries(queryClient);
      }
    },
  });

  const form = useForm({
    defaultValues: {
      [fieldName]: value || '',
    },
    onSubmit: async e => {
      await editMutation.mutateAsync(e.value as TVariables);
      // Note: State is reset in mutation's onSuccess/onError handlers
    },
  });

  const FormField = type === 'input' ? Input : Textarea;

  return (
    <Stack alignItems="center" gap={3} orientation="horizontal">
      {!isEditing && (
        <Stack alignItems="center" gap={2} orientation="horizontal">
          {/* The design-system Text component uses a polymorphic `as` prop whose TypeScript typing does not fully cover all TextAsOptions here, so the `as` value is reported as incompatible even though it is safe at runtime. Suppress this known false-positive type error. */}
          {/* @ts-expect-error */}
          <Text as={as}>{value}</Text>

          {canEdit && (
            <Button
              data-testid={`${fieldName}-edit-btn`}
              onPress={() => {
                form.reset();
                setIsEditing(prev => !prev);
              }}
              visual="unstyled"
            >
              <PencilLineIcon className={styles.editIcon} size={16} />
            </Button>
          )}
        </Stack>
      )}

      {isEditing && canEdit && (
        <form
          className={clsx(getStackClassName(), getStackClassName('horizontal'), getStackClassName('aicenter'))}
          onSubmit={async e => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit();
          }}
        >
          <form.Field name={fieldName}>
            {field => {
              const label = {
                children: fieldName,
                className: styles.capitalize,
                htmlFor: field.name,
                id: `label-${field.name}`,
              };

              return (
                <Stack gap={3} orientation="vertical">
                  <Stack alignItems="center" gap={2} orientation="horizontal">
                    <FormField
                      autoComplete="off"
                      // oxlint-disable-next-line no-autofocus
                      autoFocus={true}
                      data-testid={`input-${field.name}`}
                      id={field.name}
                      label={label}
                      name={field.name}
                      onBlur={field.handleBlur}
                      onChange={({ target }: { target: { value: string } }) => field.handleChange(target.value)}
                      value={field.state.value}
                    />

                    <form.Subscribe selector={state => [state.canSubmit, state.isSubmitting, state.isSubmitted]}>
                      {([canSubmit, isSubmitting, isSubmitted]) => (
                        <Button
                          aria-label={ariaLabel}
                          className={clsx((isSubmitting || isSubmitted) && createStyles.noPointer, styles.submitBtn)}
                          data-testid="submit-btn"
                          isDisabled={!canSubmit}
                          size="xs"
                          type="submit"
                          visual={isSubmitting || isSubmitted ? 'success' : 'unstyled'}
                        >
                          {isSubmitting || isSubmitted ? (
                            <Loader className={createStyles.spinner} size={16} />
                          ) : (
                            <Check className={styles.editIcon} />
                          )}
                        </Button>
                      )}
                    </form.Subscribe>
                  </Stack>

                  <FieldInfo field={field} />
                </Stack>
              );
            }}
          </form.Field>
        </form>
      )}
    </Stack>
  );
};

export default EditInPlace;
